import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ShieldAlert, RefreshCw, Pause, Redo, ExternalLink } from "lucide-react";

/**
 * Admin · Certificate Management — asset-agnostic pending queue + lifecycle
 * actions (approve · reject · suspend · revoke · reissue).
 */
export default function CertificateAdmin() {
  const [pending, setPending] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [lookup, setLookup] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/certificates-v2/pending");
      setPending(r.data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const openRow = async (num) => {
    try {
      const r = await api.get(`/certificates-v2/${num}`);
      setSelected(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const act = async (action, extra = {}) => {
    if (!selected) return;
    try {
      const r = await api.post(`/certificates-v2/${selected.certificate_number}/${action}`, { ...extra });
      toast.success(`${selected.certificate_number} · ${r.data.status || action}`);
      setReason("");
      await openRow(r.data.certificate_number || selected.certificate_number);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || `${action} failed`); }
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-[1500px] mx-auto px-6 py-10">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="kicker">Executive · Certificate Management</div>
            <h1 className="display text-3xl">THOM Verified™ Certificate Registry</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Approve draft certificates, suspend or revoke live certificates, and issue corrected versions. Every action is audited (actor · IP · timestamp · prev · new).</p>
          </div>
          <div className="flex gap-2 items-center">
            <Input placeholder="Look up certificate number" value={lookup} onChange={(e) => setLookup(e.target.value.toUpperCase())} className="h-9 w-64" data-testid="admin-cert-lookup" />
            <Button size="sm" variant="outline" onClick={() => openRow(lookup)} data-testid="admin-cert-open">Open</Button>
            <Button size="sm" variant="outline" onClick={load} disabled={busy}><RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh</Button>
            <Link to="/ops"><Button size="sm" variant="outline">Ops Centre</Button></Link>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_1fr] gap-4">
          {/* Pending list */}
          <div className="border border-border bg-card overflow-hidden">
            <div className="p-3 border-b border-border kicker">Pending approval ({pending.length})</div>
            <div className="max-h-[70vh] overflow-y-auto divide-y divide-border">
              {pending.length === 0 && <div className="p-6 text-sm text-muted-foreground">No drafts awaiting approval.</div>}
              {pending.map((r) => (
                <button
                  key={r.certificate_number}
                  onClick={() => openRow(r.certificate_number)}
                  data-testid={`admin-cert-row-${r.certificate_number}`}
                  className={`w-full text-left px-4 py-3 hover:bg-muted/40 ${selected?.certificate_number === r.certificate_number ? "bg-primary/5 border-l-2 border-l-primary" : ""}`}
                >
                  <div className="mono text-[11px] uppercase tracking-widest text-primary">{r.certificate_number}</div>
                  <div className="text-sm font-medium mt-0.5">{r.asset_description || r.asset_ref}</div>
                  <div className="text-[11.5px] text-muted-foreground">{r.country} · <span className="mono uppercase text-[10.5px]">{r.asset_type}</span> · {r.verification_level}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Detail */}
          <div className="border border-border bg-card p-5 max-h-[80vh] overflow-y-auto">
            {!selected && <div className="text-sm text-muted-foreground">Select a draft or look up a certificate number.</div>}
            {selected && (
              <div data-testid="admin-cert-detail">
                <div className="mono text-[11px] uppercase tracking-widest text-primary">{selected.certificate_number}</div>
                <h3 className="text-lg font-semibold mt-1">{selected.asset_description || selected.asset_ref}</h3>
                <div className="text-[11.5px] text-muted-foreground mt-0.5">{selected.country} · <span className="mono uppercase text-[10.5px]">{selected.asset_type}</span> · v{selected.version} · <span className="uppercase mono">{selected.status}</span></div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-4 text-[12.5px]">
                  <M l="Outcome" v={selected.outcome} />
                  <M l="Trust score" v={selected.trust_score} />
                  <M l="Verified at" v={selected.verified_at} />
                  <M l="Expires at" v={selected.expires_at} />
                  <M l="Officer" v={selected.issuing_officer_name} />
                  <M l="Level" v={selected.verification_level} />
                </div>

                {(selected.scope_of_checks || []).length > 0 && (
                  <div className="mt-4">
                    <div className="kicker">Scope</div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {selected.scope_of_checks.map((s, i) => <span key={i} className="text-[11px] px-2 py-1 border border-border rounded-sm">{s}</span>)}
                    </div>
                  </div>
                )}

                {(selected.material_disclosures || []).length > 0 && (
                  <div className="mt-4">
                    <div className="kicker text-amber-700">Disclosures</div>
                    <ul className="mt-1 space-y-1 text-[11.5px]">
                      {selected.material_disclosures.map((s, i) => <li key={i} className="border-l-2 border-amber-500/60 pl-2">{s}</li>)}
                    </ul>
                  </div>
                )}

                <div className="mt-5 border-t border-border pt-4">
                  <Input placeholder="Reason / note (mandatory for suspend/revoke/reissue)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="admin-cert-reason" />
                  <div className="mt-3 flex flex-wrap gap-2">
                    {selected.status === "pending_approval" && (
                      <>
                        <Button size="sm" onClick={() => act("approve", { note: reason })} data-testid="admin-cert-approve"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => act("reject", { note: reason })} data-testid="admin-cert-reject"><XCircle className="h-3.5 w-3.5 mr-1" /> Reject</Button>
                      </>
                    )}
                    {selected.status === "valid" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => reason.trim() ? act("suspend", { reason }) : toast.error("Reason required")} data-testid="admin-cert-suspend"><Pause className="h-3.5 w-3.5 mr-1" /> Suspend</Button>
                        <Button size="sm" variant="outline" onClick={() => reason.trim() ? act("revoke", { reason }) : toast.error("Reason required")} data-testid="admin-cert-revoke"><ShieldAlert className="h-3.5 w-3.5 mr-1" /> Revoke</Button>
                        <Button size="sm" variant="outline" onClick={() => reason.trim() ? act("reissue", { reason }) : toast.error("Reason required")} data-testid="admin-cert-reissue"><Redo className="h-3.5 w-3.5 mr-1" /> Reissue (new version)</Button>
                      </>
                    )}
                    <Link to={`/verify/${selected.certificate_number}`}><Button size="sm" variant="outline"><ExternalLink className="h-3.5 w-3.5 mr-1" /> Public page</Button></Link>
                  </div>
                </div>

                <div className="mt-5 border-t border-border pt-4">
                  <div className="text-xs font-semibold mb-2">Issuance history ({(selected.issuance_history || []).length})</div>
                  <ol className="space-y-1 text-[11.5px]">
                    {(selected.issuance_history || []).slice().reverse().map((h, i) => (
                      <li key={i} className="border-l-2 border-primary/40 pl-2">
                        <div className="mono uppercase text-[10px] text-primary">{h.action}</div>
                        <div className="text-muted-foreground text-[10px]">{h.at} · {h.by}</div>
                        {h.note && <div>{h.note}</div>}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const M = ({ l, v }) => (
  <><div className="mono text-[9.5px] uppercase tracking-widest text-muted-foreground">{l}</div><div>{v ?? "—"}</div></>
);
